import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { styled } from '@mui/system';

import { brandColors } from 'brandColors';
import { FeatureFlag, useFeatureFlag } from 'utils/permissions/feature-flags';
import { BANNER_HEIGHT } from '../utils/config';

/**
 * Because the content of message is provided by launch darkly
 * as raw html, we need to style certain elements, such as <a />
 * manually here.
 */
const Message = styled('h4')`
  a {
    text-decoration: underline;
  }
`;

interface Props {
  flag: DynamicBannerFlagEnabled;
  acknowledge: () => void;
}

/**
 * Displays a warning as provided by our launch darkly warning-banner flag
 */
export const DynamicBanner: React.FC<Props> = (props) => {
  const { flag, acknowledge } = props;

  let icon = null;
  const bgColor = brandColors.colors.orange[100];
  switch (flag.kind || 'notification') {
    case 'alert':
      icon = <WarningIcon style={{ color: brandColors.colors.red.DEFAULT }} />;
      break;
    case 'warning':
      icon = <WarningIcon style={{ color: brandColors.colors.red.DEFAULT }} />;
      break;
    case 'notification':
      break;
    default:
      break;
  }

  return (
    <div
      className="w-full flex items-center px-4 py-2 font-medium text-dark-100"
      style={{
        height: `${BANNER_HEIGHT}px`,
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='1440' height='400' viewBox='0 0 1440 400' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_145_460)'%3E%3Crect width='1440' height='400' fill='%23${bgColor.substring(
          1
        )}'/%3E%3Ccircle opacity='0.16' cx='65.5' cy='199.5' r='278' fill='white' stroke='%23F19100'/%3E%3Ccircle opacity='0.3' cx='65.5051' cy='199.499' r='222.3' fill='white' stroke='%23F19100'/%3E%3Ccircle opacity='0.2' cx='65.5032' cy='199.5' r='157.317' fill='white' stroke='%23F19100'/%3E%3Ccircle opacity='0.3' cx='65.5027' cy='199.5' r='55.2' fill='white' stroke='%23F19100'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_145_460'%3E%3Crect width='1440' height='400' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E")`,
        backgroundPosition: 'left center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: bgColor,
      }}
    >
      <div className="flex w-full h-full items-center space-x-2 justify-center">
        {icon}
        {/* eslint-disable-next-line react/no-danger */}
        <Message dangerouslySetInnerHTML={{ __html: flag.message || '' }} />
      </div>
      <CloseIcon
        style={{ fontSize: 18 }}
        className="cursor-pointer ml-auto"
        onClick={acknowledge}
      />
    </div>
  );
};

export default DynamicBanner;

interface DynamicBannerFlagEnabled {
  enabled: true;
  bannerId?: string;
  message?: string;
  kind?: 'alert' | 'warning' | 'notification';
}

interface DynamicBannerFlagDisabled {
  enabled: false;
}

type DynamicBannerFlag = DynamicBannerFlagEnabled | DynamicBannerFlagDisabled;

const LOCAL_STORAGE_KEY = 'dynamic_banner_acknowledged';

/**
 * Helper hook for determining if we need to show the dynamic banner that provides
 * an acknowledge function to dismiss it.
 */
export const useDynamicBanner = () => {
  const dynamicBanner = useFeatureFlag<DynamicBannerFlag>(FeatureFlag.DynamicBanner);

  // Using a set makes this self healing if we end up with duplicates
  const [acknowledgedDynamicBanner, setAcknowledgedDynamicBanner] = React.useState<string>(
    localStorage.getItem(LOCAL_STORAGE_KEY) || ''
  );

  const handleAcknowledgeDynamicBanner = React.useCallback(() => {
    if (!dynamicBanner?.enabled || !dynamicBanner.bannerId) {
      return;
    }

    localStorage.setItem(LOCAL_STORAGE_KEY, dynamicBanner.bannerId);
    setAcknowledgedDynamicBanner(dynamicBanner.bannerId);
  }, [dynamicBanner, setAcknowledgedDynamicBanner]);

  if (!dynamicBanner?.enabled || dynamicBanner.bannerId === acknowledgedDynamicBanner) {
    return { showBanner: false };
  }

  return { showBanner: true, flag: dynamicBanner, acknowledge: handleAcknowledgeDynamicBanner };
};
